import { ResponsiveBar } from '@nivo/bar'
import { IAttendanceRatingBy } from 'modules/DashboardServiceReviews/types'
import React, { useEffect, useMemo, useState } from 'react'
import { fontType, toRem } from 'shared/styles/functions'
import { useTheme } from 'styled-components'
import {
	Axis,
	AxisColorIndicator,
	AxisContainer,
	AxisLabel,
	GraphContainer,
	GraphWrapper,
} from './styles'
import { TAisleRatingProps } from './types'

const BAR_THICKNESS = 14
const BAR_PADDING = 0.35
const BAND_HEIGHT = BAR_THICKNESS / (1 - BAR_PADDING)
const CHART_TOP_MARGIN = 24
const CHART_BOTTOM_MARGIN = 40
const MAX_AISLE_LABEL_LENGTH = 22

function truncateAisleLabel(label: string): string {
	if (label.length <= MAX_AISLE_LABEL_LENGTH) return label
	return `${label.slice(0, MAX_AISLE_LABEL_LENGTH - 1).trimEnd()}…`
}

function AislesRatingGraph({
	data,
}: {
	data: IAttendanceRatingBy[]
}): JSX.Element {
	const [graphData, setGraphData] = useState<TAisleRatingProps[]>([])
	const theme = useTheme()

	useEffect(() => {
		if (!data) return
		const dataToGraphic = data.map((item) => ({
			'1 Estrela': item.summary[1],
			'2 Estrelas': item.summary[2],
			'3 Estrelas': item.summary[3],
			'4 Estrelas': item.summary[4],
			'5 Estrelas': item.summary[5],
			dataRating: item.id,
			name: item.name,
		}))
		setGraphData(dataToGraphic)
	}, [data])

	const aisleNameById = useMemo(() => {
		return graphData.reduce<Record<string, string>>((acc, item) => {
			acc[item.dataRating] = item.name
			return acc
		}, {})
	}, [graphData])

	const chartHeight = useMemo(() => {
		const bandCount = graphData.length

		return (
			(bandCount + BAR_PADDING) * BAND_HEIGHT +
			CHART_TOP_MARGIN +
			CHART_BOTTOM_MARGIN
		)
	}, [graphData])

	const containerTooltip = {
		background: theme.colors.darkGrey,
		color: 'white',
		borderRadius: 4,
		maxWidth: '300px',
		fontType: fontType(0),
		fontSize: toRem(12),
		padding: '8px 16px 8px 8px',
		wordBreak: 'break-word' as const,
	}

	const axis = [
		{ color: theme.colors.aislesRatingStarsColorOne, label: '1 Estrela' },
		{ color: theme.colors.aislesRatingStarsColorTwo, label: '2 Estrelas' },
		{ color: theme.colors.aislesRatingStarsColorThree, label: '3 Estrelas' },
		{ color: theme.colors.aislesRatingStarsColorFour, label: '4 Estrelas' },
		{ color: theme.colors.aislesRatingStarsColorFive, label: '5 Estrelas' },
	]

	return (
		<GraphWrapper>
			<GraphContainer chartHeight={chartHeight}>
				<ResponsiveBar
					data={graphData}
					keys={[
						'1 Estrela',
						'2 Estrelas',
						'3 Estrelas',
						'4 Estrelas',
						'5 Estrelas',
					]}
					indexBy={'dataRating'}
					margin={{
						top: CHART_TOP_MARGIN,
						right: 32,
						bottom: CHART_BOTTOM_MARGIN,
						left: 180,
					}}
					padding={BAR_PADDING}
					borderRadius={0}
					layout='horizontal'
					colors={[
						theme.colors.aislesRatingStarsColorOne,
						theme.colors.aislesRatingStarsColorTwo,
						theme.colors.aislesRatingStarsColorThree,
						theme.colors.aislesRatingStarsColorFour,
						theme.colors.aislesRatingStarsColorFive,
					]}
					axisTop={null}
					axisBottom={{
						tickRotation: 0,
						tickSize: 0,
						legend: '',
						format: (e) => (Math.floor(Number(e)) === e ? e : ''),
					}}
					labelTextColor='#444444'
					theme={{
						tooltip: { container: containerTooltip },
						fontFamily: 'Montserrat Regular',
						grid: {
							line: {
								stroke: 'nivo',
							},
						},
						axis: {
							ticks: {
								text: { fontSize: 10, letterSpacing: '0.48px', opacity: '0.8' },
								line: {
									stroke: 'red',
								},
							},
						},
						markers: {
							lineColor: 'red',
							lineStrokeWidth: 1,
							text: {},
						},
					}}
					label={'1 Estrela'}
					enableLabel={false}
					axisLeft={{
						tickPadding: 9,
						tickRotation: 0,
						tickSize: 0,
						format: (value) =>
							truncateAisleLabel(aisleNameById[String(value)] ?? String(value)),
					}}
					tooltip={({ id, value, indexValue }) => (
						<span>
							{`${aisleNameById[String(indexValue)] ?? indexValue} — ${id}: ${value}`}
						</span>
					)}
					animate={true}
				/>
			</GraphContainer>
			<AxisContainer>
				{React.Children.toArray(
					axis.map((axis) => (
						<Axis>
							<AxisColorIndicator color={axis.color} />
							<AxisLabel>{axis.label}</AxisLabel>
						</Axis>
					)),
				)}
			</AxisContainer>
		</GraphWrapper>
	)
}

export default AislesRatingGraph
